/**
 * Calendar Export Utilities
 * 
 * Shared utilities for generating calendar exports (Google Calendar URLs and ICS files).
 * Used by AddToCalendarDropdown component for both export options.
 * 
 * @module utils/calendarExport
 */

import { toLocalDateString } from './dateUtils';

// ============================================================================
// Types
// ============================================================================

export interface CalendarEventData {
  /** Unique event ID */
  eventId: number;
  /** Event/hearing title (used in description) */
  title: string;
  /** Event date (YYYY-MM-DD or ISO string) */
  date: string;
  /** Event time (HH:MM:SS or HH:MM) - optional for all-day events */
  time?: string;
  /** Room number or location */
  location?: string;
  /** Event description (legacy, not used in new format) */
  description?: string;
  /** Chamber (Senate/House/Joint) */
  chamber?: string;
  /** Whether event has no specific time */
  noTime?: boolean;
  /** Upon adjournment flag */
  uponAdjournment?: boolean;
  /** Bill numbers associated with event */
  billNumbers?: string[];
  /** Primary bill number for event title (e.g., "HB26-1038") */
  billNumber?: string;
  /** Primary bill title for event title (e.g., "County Commissioner Redistricting") */
  billTitle?: string;
}

// ============================================================================
// Shared Helpers
// ============================================================================

/**
 * Build event title from bill info or fallback to event title
 * Format: "HB26-1038: County Commissioner Redistricting" or just event title
 */
export const buildEventTitle = (event: CalendarEventData): string => {
  if (event.billNumber && event.billTitle) {
    return `${event.billNumber}: ${event.billTitle}`;
  } else if (event.billNumber) {
    return event.billNumber;
  } else if (event.billNumbers && event.billNumbers.length > 0) {
    return event.billNumbers[0];
  }
  return event.title || 'Legislative Event';
};

/**
 * Build event description with committee/hearing title and chamber
 */
export const buildEventDescription = (event: CalendarEventData): string => {
  let description = '';
  
  // Add time notes for upon adjournment or no time
  if (event.uponAdjournment) {
    description = 'Upon Adjournment\n\n';
  } else if (event.noTime) {
    description = 'No specific time\n\n';
  }
  
  // Add committee/hearing title
  if (event.title) {
    description += event.title;
  }
  
  // Add chamber
  if (event.chamber) {
    description += description && !description.endsWith('\n\n') ? '\n\n' : '';
    description += `Chamber: ${event.chamber}`;
  }
  
  return description;
};

/**
 * Build event location string
 */
export const buildEventLocation = (event: CalendarEventData): string => {
  return event.location 
    ? `Room ${event.location}, Colorado State Capitol`
    : 'Colorado State Capitol';
};

/**
 * Check if event should be treated as all-day
 */
export const isAllDayEvent = (event: CalendarEventData): boolean => {
  return event.noTime || event.uponAdjournment || !event.time;
};

// ============================================================================
// Google Calendar
// ============================================================================

/**
 * Format date and time for Google Calendar URL
 * Format: YYYYMMDDTHHMMSS
 */
const formatGoogleDateTime = (date: string, time?: string): string => {
  const dateOnly = date.split('T')[0];
  const [year, month, day] = dateOnly.split('-');
  
  let hours = '09';
  let minutes = '00';
  
  if (time) {
    const timeParts = time.split(':');
    if (timeParts.length >= 2) {
      hours = timeParts[0].padStart(2, '0');
      minutes = timeParts[1].padStart(2, '0');
    }
  }
  
  return `${year}${month}${day}T${hours}${minutes}00`;
};

/**
 * Calculate end time for Google Calendar (1 hour after start)
 */
const formatGoogleEndDateTime = (date: string, time?: string): string => {
  const dateOnly = date.split('T')[0];
  const [year, month, day] = dateOnly.split('-');
  
  let hours = 10;
  let minutes = '00';
  
  if (time) {
    const timeParts = time.split(':');
    if (timeParts.length >= 2) {
      hours = parseInt(timeParts[0], 10) + 1;
      minutes = timeParts[1].padStart(2, '0');
      
      if (hours >= 24) {
        hours = hours - 24;
      }
    }
  }
  
  return `${year}${month}${day}T${String(hours).padStart(2, '0')}${minutes}00`;
};

/**
 * Build Google Calendar URL for an event
 */
export const buildGoogleCalendarUrl = (event: CalendarEventData): string => {
  const allDay = isAllDayEvent(event);
  const eventTitle = buildEventTitle(event);
  const description = buildEventDescription(event);
  const location = buildEventLocation(event);
  
  const params = new URLSearchParams();
  params.set('action', 'TEMPLATE');
  params.set('text', eventTitle);
  params.set('details', description);
  params.set('location', location);
  
  if (allDay) {
    const dateOnly = event.date.split('T')[0].replace(/-/g, '');
    const startDate = new Date(event.date.split('T')[0] + 'T00:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    const endDateStr = toLocalDateString(endDate).replace(/-/g, '');
    params.set('dates', `${dateOnly}/${endDateStr}`);
  } else {
    const startDateTime = formatGoogleDateTime(event.date, event.time);
    const endDateTime = formatGoogleEndDateTime(event.date, event.time);
    params.set('dates', `${startDateTime}/${endDateTime}`);
  }
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// ============================================================================
// ICS File Generation
// ============================================================================

/**
 * Escape special characters for iCalendar format
 */
const escapeICS = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/<[^>]*>/g, ''); // Remove HTML tags
};

/**
 * Format date for iCalendar (YYYYMMDD)
 */
const formatICSDate = (date: string): string => {
  const dateOnly = date.split('T')[0];
  const [year, month, day] = dateOnly.split('-');
  return `${year}${month}${day}`;
};

/**
 * Format datetime for iCalendar (YYYYMMDDTHHMMSS)
 */
const formatICSDateTime = (date: string, time?: string): string => {
  const dateOnly = date.split('T')[0];
  const [year, month, day] = dateOnly.split('-');
  
  if (time) {
    const timeParts = time.split(':');
    if (timeParts.length >= 2 && timeParts[0] && timeParts[1]) {
      const hours = timeParts[0].padStart(2, '0');
      const minutes = timeParts[1].padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}00`;
    }
  }
  
  return `${year}${month}${day}T090000`;
};

/**
 * Generate current timestamp for DTSTAMP field (YYYYMMDDTHHMMSSZ)
 */
const generateDTSTAMP = (): string => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Calculate ICS end datetime (1 hour after start)
 */
const calculateICSEndDateTime = (date: string, time: string): string => {
  const timeParts = time.split(':');
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1] || '0', 10);
  
  let endHours = hours + 1;
  if (endHours >= 24) {
    endHours = endHours - 24;
  }
  
  const endTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  return formatICSDateTime(date, endTime);
};

/**
 * Generate ICS file content for an event
 */
export const generateICSContent = (event: CalendarEventData): string => {
  const uid = `calendar-${event.eventId}@capitolcommons.app`;
  const dtstamp = generateDTSTAMP();
  const summary = escapeICS(buildEventTitle(event));
  const allDay = isAllDayEvent(event);
  
  // Build description with ICS escaping
  let description = '';
  
  if (event.uponAdjournment) {
    description = 'Upon Adjournment\\n\\n';
  } else if (event.noTime) {
    description = 'No specific time\\n\\n';
  }
  
  if (event.title) {
    description += escapeICS(event.title);
  }
  
  if (event.chamber) {
    description += (description && !description.endsWith('\\n\\n') ? '\\n\\n' : '') + `Chamber: ${event.chamber}`;
  }
  
  const location = escapeICS(buildEventLocation(event));
  
  // Calculate start and end times
  let dtstart: string;
  let dtend: string;
  
  if (allDay) {
    dtstart = formatICSDate(event.date);
    const startDate = new Date(event.date.split('T')[0] + 'T00:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    dtend = formatICSDate(toLocalDateString(endDate));
  } else {
    dtstart = formatICSDateTime(event.date, event.time);
    dtend = calculateICSEndDateTime(event.date, event.time!);
  }
  
  // Format date for URL
  const urlDateStr = event.date.split('T')[0];
  
  // Build iCalendar content
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Capitol Commons//Calendar Export//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:Colorado Legislative Calendar',
    'X-WR-TIMEZONE:America/Denver',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    allDay ? `DTSTART;VALUE=DATE:${dtstart}` : `DTSTART:${dtstart}`,
    allDay ? `DTEND;VALUE=DATE:${dtend}` : `DTEND:${dtend}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    `URL:https://app.capitolcommons.co/public/calendar/all/${urlDateStr}`,
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR'
  ];
  
  return lines.join('\r\n');
};

/**
 * Generate filename for ICS download
 */
export const generateICSFilename = (event: CalendarEventData): string => {
  const dateStr = formatICSDate(event.date);
  return `event-${event.eventId}-${dateStr}.ics`;
};

/**
 * Download ICS file for an event
 * Creates a Blob and triggers browser download
 */
export const downloadICSFile = (event: CalendarEventData): void => {
  const content = generateICSContent(event);
  const filename = generateICSFilename(event);
  
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};
